import { BundleBuilderStoreMixinComponent } from '@theme/bundle-builder-mixin-store';
import { fetchBundleSettings } from '@theme/bundle-builder-sdk';
import { readBundleEditSnapshotForKey } from '@theme/bundle-builder-edit-session';

/** @typedef {import('./bundle-builder-mixin.js').BundleBuilderPropSpec} BundleBuilderPropSpec */
/** @typedef {import('./bundle-builder-store.js').BundleBuilderState} BundleBuilderState */
/** @typedef {import('./bundle-builder-store.js').BundleProduct} BundleProduct */

const PAGE_HEADING_ID = 'bundle-builder-page-heading';

export class BundleBuilderDataComponent extends BundleBuilderStoreMixinComponent {
  /** @type {Record<string, BundleBuilderPropSpec>} */
  props = {
    bundleProduct: {
      type: 'object',
      required: true,
      attributeName: 'data-bundle-product',
    },
    bundleProducts: {
      type: 'array',
      required: true,
      attributeName: 'data-bundle-products',
    },
    bundleCollections: {
      type: 'array',
      required: true,
      attributeName: 'data-bundle-collections',
    },
    bundleSellingPlans: {
      type: 'array',
      required: true,
      attributeName: 'data-bundle-selling-plans',
    },
    moneyFormat: {
      type: 'string',
      required: true,
      attributeName: 'data-money-format',
    },
    currency: {
      type: 'string',
      required: true,
      attributeName: 'data-currency',
    },
  };

  /** @type {BundleProduct} */
  bundleProduct;
  /** @type {BundleBuilderState['products']} */
  bundleProducts;
  /** @type {BundleBuilderState['collections']} */
  bundleCollections;
  /** @type {BundleBuilderState['sellingPlanGroups']} */
  bundleSellingPlans;
  /** @type {string} */
  moneyFormat;
  /** @type {string} */
  currency;

  gettersToRender = ['getTotalPrice', 'getTotalQuantity'];

  connectedCallback() {
    super.connectedCallback();
    const editBundleKey = new URLSearchParams(window.location.search).get('edit');

    this.updateState({
      products: this.bundleProducts,
      collections: this.bundleCollections,
      sellingPlanGroups: this.bundleSellingPlans,
      bundleProduct: this.bundleProduct,
      moneyFormat: this.moneyFormat,
      currency: this.currency,
      ...(editBundleKey
        ? {
            items: [],
            activeCartStarterId: null,
            editBundleKey,
          }
        : {
            isEditing: false,
            editBundleKey: null,
            editCartLineKeys: [],
          }),
    });
    fetchBundleSettings(this.bundleProduct.id).then((settings) => {
      this.updateState({
        bundleSettings: settings,
      });

      if (editBundleKey) {
        const snapshot = readBundleEditSnapshotForKey(editBundleKey);
        if (snapshot) {
          this.mutations.hydrateFromEdit(snapshot);
          this.#focusPageHeading();
        } else {
          this.updateState({
            items: [],
            isEditing: false,
            editBundleKey: null,
            editCartLineKeys: [],
            activeCartStarterId: null,
          });
        }
      }
    }).catch((error) => {
      this.mutations.throwError(error);
    });
  }

  #focusPageHeading() {
    requestAnimationFrame(() => {
      const heading = document.getElementById(PAGE_HEADING_ID);
      if (heading instanceof HTMLElement) {
        heading.focus();
      }
    });
  }

  onStateUpdate() {
    /** @type {Record<string, unknown>} */
    const gettersValues = {};
    for (const key of this.gettersToRender) {
      const dashKey = key.replace(/([A-Z])/g, '-$1').toLowerCase().replace(/^get-/, '');
      gettersValues[dashKey] = (/** @type {() => unknown} */ (this.getters[/** @type {keyof typeof this.getters} */ (key)]))();
    }
    this.renderObjectToHTML({
      ...{},
      ...this.state,
      ...gettersValues,
    }, 'data-state');
  }
}

if (!customElements.get('bundle-builder-data-layer-component')) {
  customElements.define('bundle-builder-data-layer-component', BundleBuilderDataComponent);
}
